import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeviceService } from '@treasury/domain/services/device/device.service';
import { tmIf } from '@treasury/utils/lit-helpers';
import { Grid, GridActiveItemChangedEvent, GridItemModel } from '@vaadin/grid';
import { columnBodyRenderer } from '@vaadin/grid/lit.js';
import { GridColumn } from '@vaadin/grid/src/vaadin-grid-column';
import '@vaadin/grid/vaadin-grid-column.js';
import '@vaadin/grid/vaadin-grid-sort-column.js';
import { PropertyValues, TemplateResult, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import at from 'lodash.at';
import { TmBaseComponent } from '../tm-base.component';
import './tm-blocking-loader';
import './tm-button';
import './tm-checkbox';
import './tm-list-header';
// eslint-disable-next-line no-duplicate-imports
import { TmSearchTermChangeEvent } from './tm-list-header';

export interface TmListColumnOptions {
    shrink: boolean;
    textAlign?: 'start' | 'end' | 'center';
    header?: string | TemplateResult;
    path?: string;
}

class TmListRow<T extends object> {
    constructor(public item: T) {}

    public highlighted = false;
}

class TmListGroupHeader<T extends object> extends TmListRow<T> {
    constructor(public text: string) {
        super({} as T);
    }
}

// We're passing the item separately from the row because the consumer usually only needs the item
// so in the common case they avoid importing the TmListRow class and adding naming boilerplate.
export type TmListColumnRenderer<T extends object = object> = (
    item: T,
    row?: TmListRow<T>,
    rowIndex?: number
) => TemplateResult;

interface TmListColumnWithOptions<T extends object = object> {
    renderer: TmListColumnRenderer<T>;
    options?: TmListColumnOptions;
}

export type TmListColumn<T extends object = object> =
    | TmListColumnRenderer<T>
    | TmListColumnWithOptions<T>;

export const tagName = 'tm-list';
@customElement(tagName)
export class TmList<T extends object = object> extends TmBaseComponent {
    @property({ attribute: false })
    public columns: TmListColumn<T>[] = [];

    @property({ attribute: false })
    public items: (T | TmListRow<T>)[] = [];

    @property({ attribute: false })
    public itemsDescriptor = 'items';

    @property({ attribute: false })
    public headerTitle?: string;

    @property({ attribute: false })
    public virtualScrollItemThreshold?: number;

    @property({ type: Array })
    public searchFields: (keyof T)[] = [];

    @property({ attribute: false })
    public searchPlaceholder = 'Search';

    @property({ type: Boolean })
    public filterButton = false;

    @property({ type: String })
    public groupBy?: keyof T;

    @property({ type: Boolean })
    public multiselect = false;

    @property({ type: Boolean })
    public disableStickyHeaders = false;

    @property({ type: Boolean })
    public disableTypeToFilter = false;

    @property({ attribute: false })
    public loading = false;

    protected verifiedPropNames: readonly (keyof this)[] = ['columns', 'items'];

    @InjectProperty()
    declare private readonly deviceService: DeviceService;

    @state()
    private searchTerm = '';

    @state()
    private rows: TmListRow<T>[] = [];

    @state()
    private selectedRows: TmListRow<T>[] = [];

    @state()
    private grid?: Grid;

    @state()
    private viewportHeight?: number;

    @state()
    private keyboardShowing = false;

    private isIos = false;

    @query('#tm-scroller')
    private scroller?: HTMLDivElement;

    private readonly defaultColumnOptions: TmListColumnOptions = {
        shrink: false,
    };

    private currentHeaderRowIndex = -1;

    private groupHeaders: { original: HTMLTableRowElement; sticky: HTMLDivElement }[] = [];

    private get stickyOffsetHeight() {
        return this.isIos && this.keyboardShowing ? 85 : 0;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        window.visualViewport?.addEventListener('resize', e => this.viewportHandler(e));
    }

    public disconnectedCallback(): void {
        window.visualViewport?.removeEventListener('resize', e => this.viewportHandler(e));
        super.disconnectedCallback();
    }

    protected firstUpdated(): void {
        if (this.deviceService.osName === 'iOS') {
            this.isIos = true;
            this.viewportHeight = window.visualViewport?.height;
        }
    }

    protected updated(_changedProperties: PropertyValues): void {
        if (_changedProperties.has('grid')) {
            this.createStickyHeaders();
        }
        if (_changedProperties.has('items') || _changedProperties.has('searchTerm')) {
            this.filterItems();
            this.scroller?.scrollTo(0, 0);
            this.createStickyHeaders();
        }
        if (_changedProperties.has('groupBy') && !this.groupBy) {
            this.removeStickyHeaders();
            this.positionStickyHeaders();
        }
        if (_changedProperties.has('selectedRows')) {
            this.dispatchEvent(
                new CustomEvent('selected-items', {
                    detail: this.selectedRows.map(row => row.item),
                    bubbles: true,
                    composed: true,
                })
            );
        }
    }

    protected render() {
        return html`<tm-section class="max-h-full flex flex-col">
            ${this.renderHeader()} ${this.renderList()}
        </tm-section>`;
    }

    private viewportHandler(e: Event) {
        const viewport = e.target;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newHeight = (viewport as any).height;

        // TODO: Do we need to check if we just rotated? That will make the viewport appear to be smaller.
        if (newHeight < (this.viewportHeight ?? 0)) {
            // Keyboard is showing
            this.keyboardShowing = true;
        } else {
            this.keyboardShowing = false;
        }
    }

    private cellPartNameGen(column: GridColumn, model: GridItemModel<TmListRow<T>>) {
        if (model.item instanceof TmListGroupHeader) {
            this.currentHeaderRowIndex = model.index;
            return 'group-header-row';
        }

        const parts = [];
        if (model.item.highlighted) {
            parts.push('highlighted');
        }
        if (model.index === this.currentHeaderRowIndex + 1) {
            parts.push('first-row-in-group');
        }

        return parts.join(' ');
    }

    private async removeStickyHeaders() {
        this.groupHeaders.forEach(({ sticky }) => sticky.remove());
        this.groupHeaders = [];
    }

    private async createStickyHeaders() {
        this.removeStickyHeaders();

        if (!this.groupBy || this.disableStickyHeaders) {
            return;
        }

        await this.updateComplete;

        const grid = this.grid?.shadowRoot;
        if (!grid) {
            return;
        }

        if (!this.scroller) {
            return;
        }

        const rows = Array.from(
            // vaadin sets the hidden property on rows that are not in the current set of items
            grid.querySelectorAll('tr:not([hidden])') as NodeListOf<HTMLTableRowElement>
        );

        // using for instead of forEach because order of the resulting array matters
        for (const row of rows) {
            const isHeader = row.querySelector('td')?.part.contains('group-header-row');
            if (!isHeader) {
                continue;
            }

            const labelSlot = row.querySelectorAll<HTMLSlotElement>('td slot');
            const noConsumerColumns = labelSlot.length < 2;
            if (noConsumerColumns) {
                continue;
            }
            const firstConsumerColumn = 1;
            const label = labelSlot[firstConsumerColumn]
                .assignedElements()[0]
                .querySelector('div')?.innerText;
            if (!label) {
                continue;
            }

            const sticky = document.createElement('div');
            sticky.textContent = label;
            sticky.id = `sticky-header-${row.id.replace('group-header-', '')}`;
            sticky.style.height = `${row.getBoundingClientRect().height}px`;
            sticky.classList.add('sticky-header');

            this.scroller.insertAdjacentElement('beforebegin', sticky);
            this.groupHeaders.push({ original: row, sticky });
        }

        await this.updateComplete;
        await this.positionStickyHeaders();
    }

    private async positionStickyHeaders() {
        if (this.disableStickyHeaders) {
            return;
        }

        // If the groupBy was previously enabled and becomes disabled reset the clipPath
        if (!this.groupBy && this.scroller) {
            this.scroller.style.clipPath = `inset(0 0 0 0)`;
        }

        if (this.groupHeaders.length === 0) {
            await this.createStickyHeaders();
        }

        this.groupHeaders.forEach(({ original, sticky }) => {
            if (!this.scroller) {
                return;
            }
            const originalTop = original.getBoundingClientRect().top;
            const scrollerTop = this.scroller.getBoundingClientRect().top + this.stickyOffsetHeight;

            if (originalTop <= scrollerTop) {
                if (sticky.style.position !== 'fixed') {
                    sticky.style.position = 'fixed';
                    const stickyHeight = sticky.getBoundingClientRect().height;
                    if (!this.scroller) {
                        return;
                    }
                    this.scroller.style.clipPath = `inset(${stickyHeight}px 0 0 0)`;
                }
                sticky.style.top = `${scrollerTop}px`;
            } else {
                sticky.style.position = 'absolute';
                const anchorStr = original.style.transform
                    .replace('translateY(', '')
                    .replace('px)', '');
                const anchor = parseInt(anchorStr);
                sticky.style.top = `${anchor - this.scroller.scrollTop}px`;
            }
        });
    }

    // The consumer-passed renderer function doesn't understand the concept of header rows, so we
    // need to wrap it in a higher-level renderer that will handle rendering the header rows,
    // while deferring to the consumer renderer for the actual item rows.
    private createRenderer(consumerRenderer: TmListColumnRenderer<T>, colIndex: number) {
        return (row: TmListRow<T>, model: GridItemModel<TmListRow<T>>) => {
            if (row instanceof TmListGroupHeader) {
                // Display the header row text in the first column...
                if (colIndex === 0) {
                    return html`<div class="flex flex-col font-medium text-xs content-center">
                        ${row.text}
                    </div>`;
                }
                // ...and empty space in the other columns
                return nothing;
            }
            // Not a header row, so render the column normally
            return consumerRenderer(row.item, row, model.index);
        };
    }

    private async groupRows() {
        const { groupBy } = this;
        if (!groupBy) {
            return;
        }

        // Group rows by the groupBy field, maintaining the order of the rows
        const groupedRows = new Map<string, TmListRow<T>[]>();
        this.rows.forEach(row => {
            const key = at(row.item, groupBy)[0]?.toString() ?? '';
            if (!groupedRows.has(key)) {
                groupedRows.set(key, []);
            }

            const groupedRow = groupedRows.get(key);
            if (groupedRow) {
                groupedRow.push(row);
            }
        });

        // Flatten the map back into a single array, inserting a header row before each group
        this.rows = [...groupedRows.entries()].flatMap(([key, rows]) => [
            new TmListGroupHeader(key),
            ...rows,
        ]);

        await this.updateComplete;
        if (this.grid) {
            this.grid?.generateCellPartNames();
        }
    }

    private filterItems() {
        const itemMatchesTerm = (item: T | TmListRow<T>) => {
            const actualItem = item instanceof TmListRow ? item.item : item;
            return at(actualItem, ...this.searchFields).some((val?) =>
                val?.toString().toLowerCase().includes(this.searchTerm.toLowerCase())
            );
        };

        this.rows = this.items
            .filter(item => !this.searchTerm || this.disableTypeToFilter || itemMatchesTerm(item))
            // before this point we allow the user to use raw items for convenience, but now we
            // need to convert them to TmListRow instances so `this.rows` is always consistent and
            // can be used internally without needing to check the type of each item
            .map(item => (item instanceof TmListRow ? item : new TmListRow<T>(item)));

        this.groupRows();
    }

    private renderHeader() {
        return html`
            <tm-list-header
                .filterButton=${this.filterButton}
                .placeholder=${this.searchPlaceholder}
                .headerTitle=${this.headerTitle}
                @filterButtonClick=${() => this.dispatchEvent(new CustomEvent('open-list-filter'))}
                @search-term-change=${(e: TmSearchTermChangeEvent) => {
                    this.searchTerm = (e.detail.value || '').trim();
                }}
                @search-field-update-complete=${() => {
                    this.positionStickyHeaders();
                }}
            >
                <slot name="header-subtitle"></slot>
            </tm-list-header>
        `;
    }

    private renderFirstColumn() {
        if (!this.multiselect) {
            // need to render an empty column so border doesn't extend to screen edge
            return html`<vaadin-grid-column auto-width .flexGrow=${0}></vaadin-grid-column>`;
        }

        return html`<vaadin-grid-selection-column
            width="auto"
            .flexGrow=${0}
            ${columnBodyRenderer<TmListRow<T>>(
                item =>
                    html`<tm-checkbox
                        class="min-w-6"
                        aria-label="Select Row"
                        ?checked=${this.selectedRows.includes(item)}
                        @checked-changed=${(e: CustomEvent<{ value: boolean }>) => {
                            const checked = e.detail.value;
                            if (checked && !this.selectedRows.includes(item)) {
                                this.selectedRows = [...this.selectedRows, item];
                            } else if (!checked && this.selectedRows.includes(item)) {
                                this.selectedRows = this.selectedRows.filter(a => a !== item);
                            }
                        }}
                    ></tm-checkbox>`,
                [this.selectedRows]
            )}
        ></vaadin-grid-selection-column>`;
    }

    private renderColumns() {
        const mainColumns = this.columns.map((c, index) => {
            const options = {
                ...this.defaultColumnOptions,
                ...(typeof c === 'function' ? {} : c.options),
            };

            const consumerRenderer = typeof c === 'function' ? c : c.renderer;
            if (options.header) {
                return html`<vaadin-grid-sort-column
                    .header=${options.header}
                    .path=${options.path}
                    .width=${options.shrink ? 'min-content' : '100px'}
                    .flexGrow=${options.shrink ? 0 : 1}
                    .textAlign=${options.textAlign ?? 'start'}
                    ${columnBodyRenderer(this.createRenderer(consumerRenderer, index), [])}
                ></vaadin-grid-sort-column>`;
            }

            return html`<vaadin-grid-column
                .width=${options.shrink ? 'min-content' : '100px'}
                .flexGrow=${options.shrink ? 0 : 1}
                .textAlign=${options.textAlign ?? 'start'}
                ${columnBodyRenderer(this.createRenderer(consumerRenderer, index), [])}
            ></vaadin-grid-column>`;
        });

        return [this.renderFirstColumn(), ...mainColumns];
    }

    private renderList() {
        if (this.loading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        if (this.items.length === 0) {
            return html`<div class="mx-auto w-full py-8 text-sm text-center text-secondary">
                No ${this.itemsDescriptor} found
            </div>`;
        }
        if (this.rows.length === 0) {
            return html`<div class="mx-auto w-full py-8 text-sm text-center text-secondary">
                No ${this.itemsDescriptor} match your search
            </div>`;
        }
        return html`
            <!-- Clips absolutely positioned elements that move above the top of scroller -->
            <div class="relative overflow-hidden flex flex-col">
                <!-- Let vaadin-grid take its full height so it doesn't try to virtualize -->
                <div
                    id="tm-scroller"
                    class="flex-1 overflow-auto"
                    @scroll=${this.positionStickyHeaders}
                >
                    <vaadin-grid
                        ${ref(grid => {
                            if (grid) {
                                this.grid = grid as Grid;
                            }
                        })}
                        .allRowsVisible=${this.virtualScrollItemThreshold
                            ? this.items.length < this.virtualScrollItemThreshold
                            : true}
                        .items=${this.rows}
                        .cellPartNameGenerator=${this.cellPartNameGen}
                        @cell-activate=${(e: CustomEvent) => {
                            if (this.multiselect) {
                                const row = e.detail.model.item;
                                if (!this.selectedRows.includes(row)) {
                                    this.selectedRows = [...this.selectedRows, row];
                                } else if (this.selectedRows.includes(row)) {
                                    this.selectedRows = this.selectedRows.filter(a => a !== row);
                                }
                            }
                        }}
                        @active-item-changed=${(e: GridActiveItemChangedEvent<TmListRow<T>>) => {
                            if (!e.detail.value) {
                                return;
                            }
                            const { item } = e.detail.value;
                            if (item) {
                                this.dispatchEvent(
                                    new CustomEvent('select-list-item', {
                                        detail: item,
                                        bubbles: true,
                                        composed: true,
                                    })
                                );
                                this.dispatchEvent(
                                    new CustomEvent('select-list-row', {
                                        detail: e.detail.value,
                                        bubbles: true,
                                        composed: true,
                                    })
                                );
                            }
                        }}
                    >
                        ${this.renderColumns()}
                    </vaadin-grid>
                </div>
                <div
                    ${tmIf(this.multiselect)}
                    class="flex justify-between px-4 py-2 border border-t"
                >
                    <tm-button
                        importance="secondary"
                        @click=${() => {
                            const allSelected = this.selectedRows.length === this.rows.length;
                            this.selectedRows = allSelected ? [] : this.rows;
                        }}
                        >Select All</tm-button
                    >
                    <slot
                        name="additional-buttons"
                        slot="additional-buttons"
                        @clear-selections=${() => {
                            this.selectedRows = [];
                        }}
                    ></slot>
                </div>
            </div>
        `;
    }

    public static get styles() {
        return [
            css`
                :host {
                    --lumo-contrast-10pct: var(--tm-color-gray-light);
                    --input-border-color: 1px solid var(--jh-color-gray-300);
                    overflow: hidden;
                }
                :host([theme='dark']) {
                    --lumo-contrast-10pct: var(--brand-400);
                }
                #tm-scroller {
                    overscroll-behavior-y: none;
                }
                .sticky-header {
                    background-color: var(--container-page);
                    color: var(--lumo-body-text-color);
                    line-height: 1rem;
                    font-family: var(--default-font-family);
                    font-size: 0.75rem;
                    font-weight: 500;
                    align-content: center;
                    width: 100%;
                    position: absolute;
                    box-sizing: border-box;
                    padding: 8px 16px 8px 17px;
                }
                vaadin-grid {
                    height: var(--grid-height, 400px);
                }
                vaadin-grid::part(selected-row-cell) {
                    background-image: none;
                    outline: none;
                    box-shadow: none;
                }
                vaadin-grid-cell-content {
                    padding: 0;
                }
                vaadin-grid::part(cell) {
                    padding: 8px 16px 8px 0;
                }
                vaadin-grid::part(first-column-cell) {
                    padding: 8px;
                    border: none;
                }
                vaadin-grid::part(group-header-row) {
                    background-color: var(--container-page);
                    margin-left: 0px;
                    padding: 8px 0;
                    pointer-events: none;
                }
                vaadin-grid::part(group-header-row first-column-cell) {
                    padding: initial;
                }
                vaadin-grid::part(group-header-row last-column-cell) {
                    width: auto;
                }
                vaadin-grid::part(highlighted) {
                    background-color: var(--container-page);
                }
                vaadin-grid::part(first-row-in-group) {
                    border: 0;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmList<object>;
    }
}
